/**
 * @fileoverview ClubOS Coaching & Players Dashboard — Head Coach / Assistant / Analyst view
 */
/* eslint-disable react/prop-types */

import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle"; import DialogContent from "@mui/material/DialogContent"; import DialogActions from "@mui/material/DialogActions";

import SoftBox from "components/SoftBox"; import SoftTypography from "components/SoftTypography"; import SoftBadge from "components/SoftBadge";
import SoftProgress from "components/SoftProgress"; import SoftButton from "components/SoftButton"; import ClickableCard from "components/ClickableCard";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout"; import DashboardNavbar from "examples/Navbars/DashboardNavbar"; import Footer from "examples/Footer";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart"; import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import { squad, matches, sessions } from "data/mockData";
import { formatDateIN, CLUB_NAME, SEASON } from "utils/indiaLocale";

// ─── Helpers ────────────────────────────────────────────────────────────────

const statusColor = (s) => ({ Available: "#82d616", Injured: "#ea0606", Suspended: "#fb8c00", International: "#17c1e8" }[s] || "#aaa");
const statusIcon  = (s) => ({ Available: "check_circle", Injured: "healing", Suspended: "block", International: "flag" }[s] || "help");
const INTENSITY_COLOR = { High: "error", Medium: "warning", Low: "success" };
const TYPE_COLOR = { Tactical: "info", Technical: "primary", Physical: "error", Recovery: "success", "Match Prep": "warning", "Match Day": "dark", "Rest Day": "secondary" };
const STATUS_COLOR = { Available: "success", Injured: "error", Suspended: "warning", International: "info" };
const POSITION_GROUPS = [
  { label: "Goalkeepers", positions: ["GK"] },
  { label: "Defenders",   positions: ["CB", "LB", "RB"] },
  { label: "Midfielders", positions: ["CDM", "CM", "CAM"] },
  { label: "Forwards",    positions: ["LW", "RW", "ST"] },
];
const OUTCOME_COLOR = { W: "success", D: "warning", L: "error" };
const TODAY = "2026-03-20";

// ─── Derived data ────────────────────────────────────────────────────────────

const firstTeamSquad  = squad.filter((p) => p.squad === "First Team");
const islMatches      = matches.filter((m) => m.outcome !== null && m.competition === "Indian Super League (ISL)");
const nextMatch       = matches.find((m) => m.outcome === null);
const upcomingFixtures = matches.filter((m) => m.outcome === null);
const last5ISL        = islMatches.slice(-5);
const daysUntil = (d) => { const n = Math.round((new Date(d) - new Date(TODAY)) / 86400000); return n === 0 ? "Today" : n === 1 ? "Tomorrow" : `In ${n} days`; };

let cum = 0; const formLabels = []; const formData = [];
islMatches.forEach((m) => { cum += m.outcome === "W" ? 3 : m.outcome === "D" ? 1 : 0; formLabels.push((m.venue === "H" ? m.away : m.home).replace("FC","").replace("United","Utd").trim()); formData.push(cum); });
const formChart = { labels: formLabels, datasets: [{ label: "Cumulative Points", color: "info", data: formData }] };

const gf = (m) => m.venue === "H" ? +m.score.split("-")[0] : +m.score.split("-")[1];
const ga = (m) => m.venue === "H" ? +m.score.split("-")[1] : +m.score.split("-")[0];
const totalGF  = islMatches.reduce((s, m) => s + gf(m), 0);
const totalGA  = islMatches.reduce((s, m) => s + ga(m), 0);
const cleanSheets = islMatches.filter((m) => ga(m) === 0).length;
const homeWins = islMatches.filter((m) => m.venue === "H" && m.outcome === "W").length;
const awayWins = islMatches.filter((m) => m.venue === "A" && m.outcome === "W").length;
const avgGPG   = islMatches.length ? (totalGF / islMatches.length).toFixed(1) : "0";
const formationCounts = {};
matches.forEach((m) => { if (m.formation) formationCounts[m.formation] = (formationCounts[m.formation] || 0) + 1; });
const formationEntries  = Object.entries(formationCounts).sort((a, b) => b[1] - a[1]);
const maxFormationCount = formationEntries[0]?.[1] || 1;
const weekSessions = sessions.filter((s) => s.date >= TODAY);

// ─── Small reusable row helpers ──────────────────────────────────────────────

const Row = ({ children }) => <SoftBox display="flex" justifyContent="space-between" alignItems="center" py={0.5} borderBottom="1px solid #f5f5f5">{children}</SoftBox>;
const Lbl = ({ children }) => <SoftTypography variant="button" color="text">{children}</SoftTypography>;
const Val = ({ children }) => <SoftTypography variant="button" fontWeight="bold" color="dark">{children}</SoftTypography>;

// ─── Detail content builders ─────────────────────────────────────────────────

const buildSquadDetail = () => POSITION_GROUPS.map(({ label, positions }) => (
  <SoftBox key={label} mb={2}>
    <SoftTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase" display="block" mb={0.5}>{label}</SoftTypography>
    {firstTeamSquad.filter((p) => positions.includes(p.position)).map((p) => (
      <Row key={p.id}>
        <SoftBox display="flex" alignItems="center" gap={0.75}>
          <Icon sx={{ color: statusColor(p.status), fontSize: "0.9rem !important" }}>{statusIcon(p.status)}</Icon>
          <SoftTypography variant="button" fontWeight="medium">{p.flag} {p.name}</SoftTypography>
        </SoftBox>
        <SoftBox display="flex" gap={0.5}>
          <SoftBadge badgeContent={p.position} color="dark" size="xs" container />
          <SoftBadge badgeContent={p.status} color={STATUS_COLOR[p.status] || "dark"} size="xs" container />
        </SoftBox>
      </Row>
    ))}
  </SoftBox>
));

const buildFixturesDetail = () => upcomingFixtures.map((m, i) => (
  <SoftBox key={m.id}>
    <SoftBox py={1.25}>
      <SoftBox display="flex" gap={0.5} mb={0.5} flexWrap="wrap">
        <SoftBadge badgeContent={m.venue === "H" ? "Home" : "Away"} color={m.venue === "H" ? "success" : "info"} size="xs" container />
        <SoftBadge badgeContent={m.competition.includes("ISL") ? "ISL" : m.competition.includes("Super Cup") ? "Super Cup" : "Cup"} color={m.competition.includes("ISL") ? "primary" : "warning"} size="xs" container />
      </SoftBox>
      <SoftTypography variant="button" fontWeight="bold" display="block">{m.home} vs {m.away}</SoftTypography>
      <SoftTypography variant="caption" color="text">{formatDateIN(m.date)} · KO {m.time} · {m.competition}</SoftTypography>
    </SoftBox>
    {i < upcomingFixtures.length - 1 && <Divider sx={{ my: 0 }} />}
  </SoftBox>
));

const buildTrainingDetail = () => sessions.map((s, i) => (
  <SoftBox key={s.id}>
    <SoftBox py={1} display="flex" gap={1.5} alignItems="flex-start">
      <SoftBox width={90} flexShrink={0}>
        <SoftTypography variant="caption" fontWeight="bold" display="block" color="dark">{new Date(s.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</SoftTypography>
        <SoftTypography variant="caption" color="text">{s.time}</SoftTypography>
      </SoftBox>
      <SoftBox flex={1}>
        <SoftBox display="flex" gap={0.5} flexWrap="wrap" mb={0.25}>
          <SoftBadge badgeContent={s.type} color={TYPE_COLOR[s.type] || "info"} size="xs" container />
          <SoftBadge badgeContent={s.intensity} color={INTENSITY_COLOR[s.intensity] || "info"} size="xs" container />
          {s.duration > 0 && <SoftBadge badgeContent={`${s.duration}min`} color="secondary" size="xs" container />}
          {s.players > 0 && <SoftBadge badgeContent={`${s.players}p`} color="dark" size="xs" container />}
        </SoftBox>
        {s.notes && <SoftTypography variant="caption" color="text">{s.notes}</SoftTypography>}
      </SoftBox>
    </SoftBox>
    {i < sessions.length - 1 && <Divider sx={{ my: 0 }} />}
  </SoftBox>
));

const buildFormDetail = () => islMatches.map((m, i) => (
  <SoftBox key={m.id}>
    <SoftBox py={1} display="flex" justifyContent="space-between" alignItems="center">
      <SoftBox>
        <SoftTypography variant="button" fontWeight="bold">{m.home} vs {m.away}</SoftTypography>
        <SoftTypography variant="caption" color="text" display="block">{formatDateIN(m.date)} · {m.formation || "—"}</SoftTypography>
      </SoftBox>
      <SoftBox display="flex" alignItems="center" gap={1}>
        <SoftTypography variant="button" fontWeight="bold">{m.score}</SoftTypography>
        <SoftBadge badgeContent={m.outcome} color={OUTCOME_COLOR[m.outcome] || "dark"} size="xs" container />
      </SoftBox>
    </SoftBox>
    {i < islMatches.length - 1 && <Divider sx={{ my: 0 }} />}
  </SoftBox>
));

const buildFormationDetail = () => (
  <SoftBox>
    {formationEntries.map(([fm, count]) => {
      const fmm = matches.filter((m) => m.formation === fm && m.outcome !== null);
      const w = fmm.filter((m) => m.outcome === "W").length;
      const d = fmm.filter((m) => m.outcome === "D").length;
      const l = fmm.filter((m) => m.outcome === "L").length;
      return (
        <SoftBox key={fm} mb={2}>
          <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <SoftTypography variant="button" fontWeight="bold">{fm}</SoftTypography>
            <SoftBox display="flex" gap={0.5}>
              <SoftBadge badgeContent={`W${w}`} color="success" size="xs" container />
              <SoftBadge badgeContent={`D${d}`} color="warning" size="xs" container />
              <SoftBadge badgeContent={`L${l}`} color="error" size="xs" container />
            </SoftBox>
          </SoftBox>
          <SoftProgress value={Math.round((count / maxFormationCount) * 100)} color="info" label={false} />
          <SoftTypography variant="caption" color="text">{count} match{count !== 1 ? "es" : ""}</SoftTypography>
        </SoftBox>
      );
    })}
  </SoftBox>
);

const buildStatsDetail = () => {
  const allPlayed = matches.filter((m) => m.outcome !== null);
  return (
    <SoftBox>
      <SoftTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase" display="block" mb={1}>ISL Only</SoftTypography>
      {[["Played", islMatches.length],["Goals Scored", totalGF],["Goals Conceded", totalGA],["Clean Sheets", cleanSheets],["Avg Goals/Game", avgGPG],["Home Wins", homeWins],["Away Wins", awayWins],["Draws", islMatches.filter((m)=>m.outcome==="D").length],["Losses", islMatches.filter((m)=>m.outcome==="L").length]].map(([lbl, val]) => <Row key={lbl}><Lbl>{lbl}</Lbl><Val>{val}</Val></Row>)}
      <Divider sx={{ my: 1.5 }} />
      <SoftTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase" display="block" mb={1}>All Competitions</SoftTypography>
      {[["Total Matches", allPlayed.length],["Wins", allPlayed.filter((m)=>m.outcome==="W").length],["Draws", allPlayed.filter((m)=>m.outcome==="D").length],["Losses", allPlayed.filter((m)=>m.outcome==="L").length]].map(([lbl, val]) => <Row key={lbl}><Lbl>{lbl}</Lbl><Val>{val}</Val></Row>)}
    </SoftBox>
  );
};

// ─── KPI modal helpers ───────────────────────────────────────────────────────

const KPI_MODAL_LABELS = { available: "Available Players", injured: "Injured Players", suspendedIntl: "Suspended / International", nextMatch: "Next Match Details" };

function KpiModalBody({ type }) {
  const players = { available: firstTeamSquad.filter((p) => p.status === "Available"), injured: firstTeamSquad.filter((p) => p.status === "Injured"), suspendedIntl: firstTeamSquad.filter((p) => p.status === "Suspended" || p.status === "International") };
  if (type === "nextMatch" && nextMatch) {
    return (
      <SoftBox>
        <SoftTypography variant="h6" fontWeight="bold" mb={1}>{nextMatch.home} vs {nextMatch.away}</SoftTypography>
        <SoftTypography variant="body2" color="text" mb={0.5}>{formatDateIN(nextMatch.date)} · KO {nextMatch.time} · {nextMatch.competition}</SoftTypography>
        <SoftBadge badgeContent={nextMatch.venue === "H" ? "Home" : "Away"} color={nextMatch.venue === "H" ? "success" : "info"} size="sm" container />
        <Divider sx={{ my: 1.5 }} />
        <SoftTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase" display="block" mb={0.5}>Formation History</SoftTypography>
        {formationEntries.map(([f, c]) => <SoftTypography key={f} variant="caption" color="text" display="block">{f} — {c} match{c !== 1 ? "es" : ""}</SoftTypography>)}
      </SoftBox>
    );
  }
  const list = players[type] || [];
  const color = { available: "success", injured: "error", suspendedIntl: null }[type];
  return list.length === 0 ? <SoftTypography variant="body2" color="text">None currently.</SoftTypography> : (
    <>{list.map((p) => <Row key={p.id}><SoftTypography variant="button" fontWeight="medium">{p.flag} {p.name}</SoftTypography><SoftBox display="flex" gap={0.5}><SoftBadge badgeContent={p.position} color="dark" size="xs" container /><SoftBadge badgeContent={p.status} color={color || STATUS_COLOR[p.status] || "dark"} size="xs" container /></SoftBox></Row>)}</>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

function CoachingDashboard() {
  const [kpiModal, setKpiModal] = useState(null);
  const availableCount  = firstTeamSquad.filter((p) => p.status === "Available").length;
  const injuredCount    = firstTeamSquad.filter((p) => p.status === "Injured").length;
  const unavailableCount = firstTeamSquad.filter((p) => p.status === "Suspended" || p.status === "International").length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>

        {/* ── Page Header ───────────────────────────────────────────────── */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Coaching &amp; Players</SoftTypography>
            <SoftTypography variant="body2" color="text">{CLUB_NAME} &middot; Coaching Staff &middot; Season {SEASON}</SoftTypography>
          </SoftBox>
          <SoftBox>
            <SoftTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>ISL Form (last 5)</SoftTypography>
            <SoftBox display="flex" gap={0.5}>
              {last5ISL.map((m) => (
                <Tooltip key={m.id} title={`${m.venue === "H" ? m.away : m.home} — ${m.score}`}>
                  <SoftBox width="2rem" height="2rem" borderRadius="md" display="flex" alignItems="center" justifyContent="center"
                    sx={{ backgroundColor: ({ palette }) => palette[OUTCOME_COLOR[m.outcome]]?.main || "#aaa", cursor: "default" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "white !important" }}>{m.outcome}</SoftTypography>
                  </SoftBox>
                </Tooltip>
              ))}
            </SoftBox>
          </SoftBox>
        </SoftBox>

        {/* ── KPI Row ──────────────────────────────────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            {[
              { key: "available",     title: { text: "Available" },        count: String(availableCount),   pct: { color: "success", text: "ready to train/play" }, icon: { color: "success", component: "check_circle" } },
              { key: "injured",       title: { text: "Injured" },          count: String(injuredCount),     pct: { color: "error",   text: "in rehabilitation" },   icon: { color: "error",   component: "healing" } },
              { key: "suspendedIntl", title: { text: "Suspended / Intl" }, count: String(unavailableCount), pct: { color: "warning", text: "unavailable this week" },icon: { color: "warning", component: "block" } },
              { key: "nextMatch",     title: { text: "Next Match" },       count: nextMatch ? daysUntil(nextMatch.date) : "—", pct: { color: "info", text: nextMatch ? (nextMatch.venue === "H" ? nextMatch.away : nextMatch.home) : "No fixture" }, icon: { color: "info", component: "sports_soccer" } },
            ].map(({ key, title, count, pct, icon }) => (
              <Grid item xs={12} sm={6} lg={3} key={key}>
                <SoftBox onClick={() => setKpiModal(key)} sx={{ cursor: "pointer", borderRadius: "lg", transition: "transform 0.18s, box-shadow 0.18s", "&:hover": { transform: "translateY(-3px)", boxShadow: "0 10px 28px rgba(0,0,0,0.13)" } }}>
                  <MiniStatisticsCard title={title} count={count} percentage={pct} icon={icon} />
                </SoftBox>
              </Grid>
            ))}
          </Grid>
        </SoftBox>

        {/* ── Row 2: Availability Matrix + Upcoming Fixtures ───────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <ClickableCard title="Squad Availability Matrix" icon="groups" iconColor="info" tooltip="Click to see full squad availability" detailContent={buildSquadDetail()}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={2}>Squad Availability Matrix</SoftTypography>
                  {POSITION_GROUPS.map((group) => {
                    const gp = firstTeamSquad.filter((p) => group.positions.includes(p.position));
                    return (
                      <SoftBox key={group.label} mb={2}>
                        <SoftTypography variant="caption" fontWeight="bold" color="text" textTransform="uppercase" display="block" mb={0.75}>{group.label}</SoftTypography>
                        <SoftBox display="flex" flexWrap="wrap" gap={0.75}>
                          {gp.map((p) => (
                            <Tooltip key={p.id} title={`${p.name} — ${p.position} — ${p.status}`}>
                              <SoftBox px={1.25} py={0.5} borderRadius="md" display="flex" alignItems="center" gap={0.5}
                                sx={{ backgroundColor: statusColor(p.status) + "22", border: `1.5px solid ${statusColor(p.status)}`, cursor: "default" }}>
                                <Icon sx={{ color: statusColor(p.status), fontSize: "0.85rem !important" }}>{statusIcon(p.status)}</Icon>
                                <SoftTypography variant="caption" fontWeight="medium" sx={{ color: "#344767" }}>{p.flag} {p.name.split(" ")[0]}</SoftTypography>
                              </SoftBox>
                            </Tooltip>
                          ))}
                        </SoftBox>
                      </SoftBox>
                    );
                  })}
                  <SoftBox display="flex" gap={2} mt={1} flexWrap="wrap">
                    {["Available","Injured","Suspended","International"].map((s) => (
                      <SoftBox key={s} display="flex" alignItems="center" gap={0.5}>
                        <SoftBox width="0.75rem" height="0.75rem" borderRadius="xs" sx={{ backgroundColor: statusColor(s) }} />
                        <SoftTypography variant="caption" color="text">{s}</SoftTypography>
                      </SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
              </ClickableCard>
            </Grid>

            <Grid item xs={12} lg={5}>
              <ClickableCard title="Upcoming Fixtures" icon="calendar_today" iconColor="primary" tooltip="Click to see all upcoming fixtures" detailContent={buildFixturesDetail()}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={2}>Upcoming Fixtures</SoftTypography>
                  {upcomingFixtures.map((m, i) => (
                    <SoftBox key={m.id}>
                      <SoftBox display="flex" alignItems="center" py={1.25} gap={1.5}>
                        <SoftBox bgColor={m.venue === "H" ? "success" : "info"} variant="gradient" width="2.2rem" height="2.2rem" borderRadius="md" flexShrink={0} display="flex" alignItems="center" justifyContent="center">
                          <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "white !important" }}>{m.venue}</SoftTypography>
                        </SoftBox>
                        <SoftBox flex={1}>
                          <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <SoftTypography variant="button" fontWeight="bold">{m.home} vs {m.away}</SoftTypography>
                            <SoftBadge badgeContent={m.competition.includes("ISL") ? "ISL" : m.competition.includes("Super Cup") ? "Super Cup" : "Cup"} color={m.competition.includes("ISL") ? "primary" : "warning"} size="xs" container />
                          </SoftBox>
                          <SoftTypography variant="caption" color="text">{formatDateIN(m.date)} &middot; KO {m.time}</SoftTypography>
                        </SoftBox>
                      </SoftBox>
                      {i < upcomingFixtures.length - 1 && <Divider sx={{ my: 0 }} />}
                    </SoftBox>
                  ))}
                </SoftBox>
              </ClickableCard>
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Row 3: Training Sessions + ISL Form Chart ─────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={5}>
              <ClickableCard title="Weekly Training Schedule" icon="fitness_center" iconColor="warning" tooltip="Click to see full training schedule" detailContent={buildTrainingDetail()}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={2}>This Week&apos;s Training</SoftTypography>
                  {weekSessions.map((s, i) => (
                    <SoftBox key={s.id}>
                      <SoftBox display="flex" alignItems="flex-start" py={1} gap={1.5}>
                        <SoftBox width={80} flexShrink={0}>
                          <SoftTypography variant="caption" fontWeight="bold" display="block" color="dark">
                            {new Date(s.date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                          </SoftTypography>
                          <SoftTypography variant="caption" color="text">{s.time}</SoftTypography>
                        </SoftBox>
                        <SoftBox flex={1}>
                          <SoftBox display="flex" gap={0.5} flexWrap="wrap" mb={0.25}>
                            <SoftBadge badgeContent={s.type} color={TYPE_COLOR[s.type] || "info"} size="xs" container />
                            <SoftBadge badgeContent={s.intensity} color={INTENSITY_COLOR[s.intensity] || "info"} size="xs" container />
                            {s.duration > 0 && <SoftBadge badgeContent={`${s.duration}min`} color="secondary" size="xs" container />}
                            {s.players > 0 && <SoftBadge badgeContent={`${s.players}p`} color="dark" size="xs" container />}
                          </SoftBox>
                          {s.notes && <SoftTypography variant="caption" color="text">{s.notes}</SoftTypography>}
                        </SoftBox>
                      </SoftBox>
                      {i < weekSessions.length - 1 && <Divider sx={{ my: 0 }} />}
                    </SoftBox>
                  ))}
                </SoftBox>
              </ClickableCard>
            </Grid>

            <Grid item xs={12} lg={7}>
              <ClickableCard title="ISL Season Form — Full Match Log" icon="show_chart" iconColor="info" tooltip="Click to see full ISL match log" detailContent={buildFormDetail()}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={0.5}>ISL Season Form</SoftTypography>
                  <SoftTypography variant="caption" color="text" display="block" mb={2}>Cumulative points per match — {SEASON}</SoftTypography>
                  <GradientLineChart chart={formChart} height="12rem" />
                  <SoftBox display="flex" gap={0.5} mt={2} justifyContent="center" flexWrap="wrap">
                    {islMatches.slice(-5).map((m) => (
                      <Tooltip key={m.id} title={`${m.venue === "H" ? m.away : m.home} — ${m.score}`}>
                        <SoftBox width="1.8rem" height="1.8rem" borderRadius="sm" display="flex" alignItems="center" justifyContent="center"
                          sx={{ backgroundColor: ({ palette }) => palette[OUTCOME_COLOR[m.outcome]]?.main || "#aaa", cursor: "default" }}>
                          <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "white !important", fontSize: "0.65rem" }}>{m.outcome}</SoftTypography>
                        </SoftBox>
                      </Tooltip>
                    ))}
                  </SoftBox>
                </SoftBox>
              </ClickableCard>
            </Grid>
          </Grid>
        </SoftBox>

        {/* ── Row 4: Formation Summary + Key Stats ──────────────────────── */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={4}>
              <ClickableCard title="Formation Summary" icon="dashboard_customize" iconColor="dark" tooltip="Click to see formation results breakdown" detailContent={buildFormationDetail()}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={2}>Formation Usage</SoftTypography>
                  {formationEntries.map(([formation, count]) => (
                    <SoftBox key={formation} mb={2}>
                      <SoftBox display="flex" justifyContent="space-between" mb={0.5}>
                        <SoftTypography variant="button" fontWeight="medium">{formation}</SoftTypography>
                        <SoftTypography variant="caption" color="text">{count} match{count !== 1 ? "es" : ""}</SoftTypography>
                      </SoftBox>
                      <SoftProgress value={Math.round((count / maxFormationCount) * 100)} color="info" label={false} />
                    </SoftBox>
                  ))}
                </SoftBox>
              </ClickableCard>
            </Grid>

            <Grid item xs={12} lg={8}>
              <ClickableCard title="Key Stats — ISL vs All Competitions" icon="bar_chart" iconColor="success" tooltip="Click to see complete stats breakdown" detailContent={buildStatsDetail()}>
                <SoftBox p={3}>
                  <SoftTypography variant="h6" fontWeight="medium" mb={2}>Key Stats This Season (ISL)</SoftTypography>
                  <Grid container spacing={2}>
                    {[
                      ["Goals Scored", totalGF, "sports_soccer", "success"],
                      ["Goals Conceded", totalGA, "gpp_bad", "error"],
                      ["Clean Sheets", cleanSheets, "shield", "info"],
                      ["Avg Goals/Game", avgGPG, "bar_chart", "primary"],
                      ["Wins at Home", homeWins, "home", "warning"],
                      ["Wins Away", awayWins, "flight_takeoff", "dark"],
                    ].map(([label, value, icon, color]) => (
                      <Grid item xs={6} sm={4} key={label}>
                        <SoftBox p={2} borderRadius="lg" textAlign="center" sx={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}>
                          <SoftBox bgColor={color} variant="gradient" width="2.5rem" height="2.5rem" borderRadius="md" display="flex" alignItems="center" justifyContent="center" mx="auto" mb={1}>
                            <Icon sx={{ color: "white" }}>{icon}</Icon>
                          </SoftBox>
                          <SoftTypography variant="h5" fontWeight="bold" color="dark">{value}</SoftTypography>
                          <SoftTypography variant="caption" color="text">{label}</SoftTypography>
                        </SoftBox>
                      </Grid>
                    ))}
                  </Grid>
                </SoftBox>
              </ClickableCard>
            </Grid>
          </Grid>
        </SoftBox>

      </SoftBox>

      {/* ── KPI Detail Dialog ─────────────────────────────────────────────── */}
      <Dialog open={Boolean(kpiModal)} onClose={() => setKpiModal(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <SoftTypography variant="h6" fontWeight="bold" component="span">{KPI_MODAL_LABELS[kpiModal] || ""}</SoftTypography>
        </DialogTitle>
        <DialogContent><KpiModalBody type={kpiModal} /></DialogContent>
        <DialogActions>
          <SoftButton variant="gradient" color="dark" size="small" onClick={() => setKpiModal(null)}>Close</SoftButton>
        </DialogActions>
      </Dialog>

      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />
    </DashboardLayout>
  );
}

export default CoachingDashboard;
